import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { Form, Formik, type FormikErrors } from 'formik';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { DEFAULT_LANGUAGE } from '../shared-constants';
import { SingleUpload } from '../../common/components';
import { FileUploadType } from '../../common/enums';
import { type File } from '../../common/core-api';
import { type Translation } from '../../common/interfaces';
import { useFile } from '../../hooks';
import { GET_PAGE_CMS_EDITOR_DATA, type PageCmsEditorDataQuery } from '../../graphql/queries';
import { type EditCustomPageProps } from '../../features';
import { type PageCmsPanels } from './common/panel-types';
import { AddElementMenu } from './components/AddElementMenu';
import { PanelGridLayout } from './components/PanelGridLayout';
import { customPageTitleMessages, CustomPageTitleRow } from './components/CustomPageTitleRow';
import {
  BannerRow,
  DeletePageRow,
  DesignTabInfo,
  DesignTabLink,
  GridArea,
  IntroText,
  PreviewText,
  SecondaryButton,
  StyledLoadingIndicator,
  Toolbar,
  ToolbarCenter,
  ToolbarLeft,
  ToolbarRight,
  Wrapper
} from './CustomPage.styled';

const DESIGN_TAB_URL = '/design/admin_viewdesign';

const messages = defineMessages({
  intro: {
    id: 'newCustomPageIntro',
    defaultMessage:
      'Custom pages can display elements with text, carousels, images and activity lists in any order you like.'
  },
  preview: {
    id: 'editCustomPagePreview',
    defaultMessage: 'Preview'
  },
  savePage: {
    id: 'editCustomPageSavePage',
    defaultMessage: 'Save Page'
  },
  publish: {
    id: 'publish',
    defaultMessage: 'Publish'
  },
  unpublish: {
    id: 'unpublish',
    defaultMessage: 'Unpublish'
  },
  useDifferentBanner: {
    id: 'editCustomPageUseDifferentBanner',
    defaultMessage: 'Use a different banner'
  },
  attachImage: {
    id: 'attachImage',
    defaultMessage: 'Attach image'
  },
  bannerUploadInstructions: {
    id: 'editCustomPageBannerUploadInstructions',
    defaultMessage: 'Drag & Drop or manually attach an image'
  },
  bannerUploadDimensions: {
    id: 'editCustomPageBannerUploadDimensions',
    defaultMessage: 'We recommend that your banner is 980 pixels wide and 124 pixels high.'
  },
  designTabLinkText: {
    id: 'editCustomPageDesignTabLink',
    defaultMessage: 'Design tab'
  },
  deletePage: {
    id: 'editCustomPageDeletePage',
    defaultMessage: 'Delete page'
  }
});

interface EditPageFormValues {
  language: string;
  pageNavTitle: string;
  isExclude: boolean;
  isHidden: boolean;
  useDifferentBanner: boolean;
  bannerImage: File | null;
  translations: Translation[];
  panels: PageCmsPanels;
}

const buildInitialValues = (
  page: NonNullable<PageCmsEditorDataQuery['pageCMS']> | null | undefined
): EditPageFormValues => ({
  language: DEFAULT_LANGUAGE,
  pageNavTitle: page?.pageNavTitle ?? '',
  isExclude: Boolean(page?.isExclude),
  isHidden: Boolean(page?.isHidden),
  useDifferentBanner: Boolean(page?.bannerImage),
  bannerImage: page?.bannerImage ?? null,
  translations: (page?.translations ?? [])
    .filter((translation): translation is NonNullable<typeof translation> => translation !== null)
    .map((translation) => ({
      id: translation.id,
      communityId: translation.communityId,
      field: translation.field,
      iso: translation.iso,
      // Align objectId with the field-based translate id used by TranslatableInput.
      objectId: translation.field,
      objectType: translation.objectType,
      value: translation.value
    })),
  panels: page?.panels ?? []
});

export const EditCustomPage = ({ pageId, community }: EditCustomPageProps) => {
  const intl = useIntl();
  const [, uploadExtensions] = useFile();

  const { data: pageCmsEditorData, loading } = useQuery<PageCmsEditorDataQuery>(GET_PAGE_CMS_EDITOR_DATA, {
    variables: { pageId: Number(pageId), communityId: Number(community.id) }
  });

  useEffect(() => {
    if (pageCmsEditorData) {
      console.log('[editCustomPage] fetched data', pageCmsEditorData);
    }
  }, [pageCmsEditorData]);

  const requiredMessage = intl.formatMessage(customPageTitleMessages.requiredTitleError);

  const handleAddElement = useCallback(
    (type: string) => {
      console.log('[editCustomPage] add element', { pageId, type });
    },
    [pageId]
  );

  const initialValues = useMemo(() => buildInitialValues(pageCmsEditorData?.pageCMS), [pageCmsEditorData]);

  const validate = useCallback(
    (values: EditPageFormValues) => {
      const errors: FormikErrors<EditPageFormValues> = {};
      if (!values.pageNavTitle.trim()) {
        errors.pageNavTitle = requiredMessage;
      }
      return errors;
    },
    [requiredMessage]
  );

  return (
    <Wrapper data-testid="edit-page-view">
      {loading ? (
        <StyledLoadingIndicator />
      ) : (
        <>
          <IntroText>{intl.formatMessage(messages.intro)}</IntroText>
          <Formik<EditPageFormValues>
            enableReinitialize
            initialValues={initialValues}
            validate={validate}
            onSubmit={async () => undefined}
          >
            {({ values, setFieldValue }) => {
              const handleSaveClick = () => {
                console.log('[editCustomPage] save click', {
                  pageId,
                  title: values.pageNavTitle,
                  isExclude: values.isExclude,
                  isHidden: values.isHidden,
                  customBannerEnabled: values.useDifferentBanner,
                  bannerFileId: values.bannerImage?.id,
                  panels: values.panels
                });
              };

              const handlePublishToggle = () => {
                const nextHidden = !values.isHidden;
                setFieldValue('isHidden', nextHidden);
                console.log('[editCustomPage] publish toggle', {
                  pageId,
                  nextState: nextHidden ? 'unpublished' : 'published'
                });
              };

              const handleDeleteClick = () => {
                console.log('[editCustomPage] delete page', { pageId });
              };

              return (
                <Form>
                  <CustomPageTitleRow />

                  <Toolbar id="admin_page_instance_toolbar">
                    <ToolbarLeft>
                      <AddElementMenu onAddElement={handleAddElement} />
                    </ToolbarLeft>

                    <ToolbarCenter>
                      <PreviewText>{intl.formatMessage(messages.preview)}</PreviewText>
                    </ToolbarCenter>

                    <ToolbarRight>
                      <Button type="button" priority="primary" onClick={handlePublishToggle}>
                        {intl.formatMessage(values.isHidden ? messages.publish : messages.unpublish)}
                      </Button>
                      <Button type="submit" priority="primary" onClick={handleSaveClick}>
                        {intl.formatMessage(messages.savePage)}
                      </Button>
                    </ToolbarRight>
                  </Toolbar>

                  <BannerRow>
                    <Checkbox
                      label={intl.formatMessage(messages.useDifferentBanner)}
                      checked={values.useDifferentBanner}
                      onChange={(event) => setFieldValue('useDifferentBanner', event.target.checked)}
                    />

                    {values.useDifferentBanner && (
                      <SingleUpload
                        buttonLabel={intl.formatMessage(messages.attachImage)}
                        fileTypes={uploadExtensions.image}
                        type={FileUploadType.PAGE_HEADER}
                        labelId="custom-page-banner-upload"
                        describedBy="custom-page-banner-upload-instructions"
                        currentFile={values.bannerImage}
                        onFileUpload={(file) => setFieldValue('bannerImage', file)}
                        onFileDelete={() => setFieldValue('bannerImage', null)}
                      >
                        <span id="custom-page-banner-upload-instructions">
                          <p>{intl.formatMessage(messages.bannerUploadInstructions)}</p>
                          <p>{intl.formatMessage(messages.bannerUploadDimensions)}</p>
                        </span>
                      </SingleUpload>
                    )}
                  </BannerRow>

                  <DesignTabInfo>
                    <FormattedMessage
                      id="editCustomPageDesignTabInfo"
                      defaultMessage="By default your page will use the community banner set in the {designTab}."
                      values={{
                        designTab: (
                          <DesignTabLink href={DESIGN_TAB_URL}>
                            {intl.formatMessage(messages.designTabLinkText)}
                          </DesignTabLink>
                        )
                      }}
                    />
                  </DesignTabInfo>

                  <GridArea>
                    <PanelGridLayout />
                  </GridArea>

                  <DeletePageRow>
                    <SecondaryButton type="button" priority="secondary" onClick={handleDeleteClick}>
                      {intl.formatMessage(messages.deletePage)}
                    </SecondaryButton>
                  </DeletePageRow>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </Wrapper>
  );
};
